/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { routes, routeHref } from '@/route-paths';

/**
 * RGBARRY-CUSTOMIZATION: production-identical category menu links (W-23875749; follow-up to
 * W-23754187).
 *
 * Production baggallini.com serves category pages at platform SEO slug paths (`/crossbody-handbags`,
 * `/anti-theft/shop-anti-theft`), while Storefront Next's default category route is the id-based
 * `/category/:categoryId`. The `int_urlresolver_scapi` endpoint resolves each category id to its
 * canonical slug path using the same URL-Rules engine production uses; the layout loader batches all
 * mega-menu category ids into one call and passes the resulting `categoryId -> slugPath` map down
 * through this context.
 *
 * `useCategoryHref()` returns a resolver that prefers the resolved slug path and falls back to the
 * id-based route when a category is unresolved (endpoint miss, resolver outage, or a category not in
 * the batch). Slug navigations are handled by the legacy-URL catch-all route (see
 * `lib/api/url-mapping.server.ts`), so a resolved link lands on the same page the id-based link would.
 */
export type CategoryUrlMap = Record<string, string>;

const CategoryHrefContext = createContext<CategoryUrlMap>({});

export function CategoryHrefProvider({ value, children }: { value: CategoryUrlMap; children: ReactNode }): ReactNode {
    return <CategoryHrefContext value={value}>{children}</CategoryHrefContext>;
}

/**
 * Returns a stable function mapping a category id to its href: the resolved production slug path when
 * available, otherwise the id-based `/category/:categoryId` route as a graceful fallback.
 */
// oxlint-disable-next-line react-refresh/only-export-components
export function useCategoryHref(): (categoryId: string) => string {
    const map = useContext(CategoryHrefContext);
    return useMemo(() => (categoryId: string) => map[categoryId] ?? routeHref(routes.category, { categoryId }), [map]);
}
